import { SolverEngine } from '../../types/planung-graphql-client-defs';
import { parseTimefoldScoreString, type Score3 } from './parseTimefoldScoreAnalysis';

/** Mathplan reports ~1e99 for "no incumbent yet" — don't render that as a percentage. */
export const GAP_INITIALIZING_THRESHOLD = 1e50;

export const gapFormatter = new Intl.NumberFormat('de-DE', {
  minimumFractionDigits: 5,
  maximumFractionDigits: 5,
});

export type BreakdownRow = {
  name: string;
  hardScore: number;
  mediumScore: number;
  softScore: number;
  matchCount: number;
  sampleJustifications?: string[];
};

export type ParsedScoreDetails = {
  engine: string;
  status?: {
    name?: string;
    solverStatus?: string | null;
    score?: Score3 | null;
  };
  monitor?: {
    pollCount?: number;
    lastPolledAt?: string;
    scoreLastChangedAt?: string | null;
    unchangedForSeconds?: number;
  };
  breakdownLight?: { top: BreakdownRow[] };
  breakdownFinal?: { top: BreakdownRow[] };
};

export function coerceStatusScore(raw: unknown): Score3 | null | undefined {
  if (raw === undefined) {
    return undefined;
  }
  if (raw === null) {
    return null;
  }
  if (typeof raw === 'object' && 'hard' in raw && 'medium' in raw && 'soft' in raw) {
    const h = (raw as { hard: unknown }).hard;
    const m = (raw as { medium: unknown }).medium;
    const s = (raw as { soft: unknown }).soft;
    if (typeof h === 'number' && typeof m === 'number' && typeof s === 'number') {
      return { hard: h, medium: m, soft: s };
    }
  }
  return parseTimefoldScoreString(raw) ?? undefined;
}

/**
 * Parse the solver's `scoreDetails` JSON payload.
 * Returns null for empty input, 'error' when the JSON can't be parsed, otherwise the structured details.
 */
export function parseScoreDetails(scoreDetails: string | null | undefined): ParsedScoreDetails | 'error' | null {
  if (!scoreDetails) {
    return null;
  }
  try {
    const o = JSON.parse(scoreDetails) as Record<string, unknown>;
    if (o.engine === 'TIMEFOLD') {
      return o as ParsedScoreDetails;
    }
    if (typeof o.name === 'string' && (o.solverStatus !== undefined || o.score !== undefined)) {
      return {
        engine: 'TIMEFOLD',
        status: {
          name: o.name,
          solverStatus:
            o.solverStatus === null ? null : typeof o.solverStatus === 'string' ? o.solverStatus : undefined,
          score: coerceStatusScore(o.score),
        },
      };
    }
    return null;
  } catch {
    return 'error';
  }
}

export function formatClock(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const mm = String(m).padStart(2, '0');
  const ss = String(s).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${m}:${ss}`;
}

/** i18n key for a solver engine's display name. */
export function solverEngineLabelKey(engine: SolverEngine | null | undefined): string {
  switch (engine) {
    case SolverEngine.Mathplan:
      return 'solver.engine.mathplan.name';
    case SolverEngine.Asc:
      return 'solver.engine.asc.name';
    case SolverEngine.Opent8:
      return 'solver.engine.opent8.name';
    default:
      return 'solver.engine.timefold.name';
  }
}

/**
 * True once the running job holds a plan without conflicts: every card placed and no hard
 * violation. `solutionFound` alone is not enough for Timefold/cp-sat — the backend sets it as soon
 * as an incumbent with a score exists, which may still break hard rules or leave cards unplaced.
 * For engines without a hard/medium/soft score (Mathplan: `withMipSolution`, a feasible MIP
 * solution) `solutionFound` is taken as is.
 */
export function isConflictFreeSolution(job: {
  engine: SolverEngine;
  solutionFound: boolean;
  scoreDetails?: string | null;
  unplacedCount: number;
}): boolean {
  if (!job.solutionFound || job.unplacedCount > 0) {
    return false;
  }
  if (job.engine !== SolverEngine.Timefold) {
    return true;
  }
  const details = parseScoreDetails(job.scoreDetails);
  // The backend normalizes the score to {hard, medium, soft}; coerce anyway in case a raw string slips through.
  const score = details && details !== 'error' ? coerceStatusScore(details.status?.score) : null;
  return score != null && score.hard === 0;
}
